import React, { useCallback, useState, useMemo } from 'react';
import { useMutation } from 'redux-query-react';
import cn from 'classnames';
import { RouteComponentProps } from '@reach/router';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { nanoid } from 'nanoid/index';
import { Marquee, Text, HeaderText, Button, LinkButton } from '@el8/vital';
import { WithFormikConfig, FormikProps, withFormik } from 'formik';

import { CreatePaymentIntentResponseBody, ConfirmPaymentIntentResponseBody } from 'QueryTypes';
import { Box } from 'components';
import LockIcon from 'components/icons/LockIcon';
import { paymentIntentQuery, confirmPaymentIntentQuery } from 'modules/payments';
import { formatDollars, logEvent, notify } from 'utils';
import { fixedSubmitForm } from 'utils/formik';
import { checkSuccess } from 'utils/errors';
import PaymentMethodForm, {
  paymentMethodValidationSchema,
  paymentMethodInitialValues,
  PaymentMethodFormValues,
} from 'views/payments/shared/PaymentMethodForm';
import {
  useConfirmStripeCardPayment,
  makeConfirmCardPaymentData,
} from 'views/payments/shared/helpers';

import PaymentsHeader from '../../shared/PaymentsHeader';
import RootContainer from '../../shared/RootContainer';
import { PaymentDetailsValues } from '../helperTypes';
import styles from './SubmitPaymentStep.less';

type OwnProps = {
  initialValues: PaymentMethodFormValues;
  paymentDetails: PaymentDetailsValues;
  onEditPaymentDetails: (values: PaymentMethodFormValues) => void;
};

type NonFormikProps = OwnProps & RouteComponentProps;

type Props = NonFormikProps & FormikProps<PaymentMethodFormValues>;

const withFormikConfig: WithFormikConfig<NonFormikProps, PaymentMethodFormValues> = {
  mapPropsToValues({ initialValues }) {
    return initialValues || { ...paymentMethodInitialValues };
  },
  handleSubmit() {
    // Required by Typescript, but we can't actually perform the submission here because
    // we can't access `stripe` (from useStripe) or `elements` (from useElements) inside of
    // this function. So instead, we handle submission inside the component.
  },
  validationSchema: paymentMethodValidationSchema,
};

const SubmitPaymentStep: React.FC<Props> = ({
  isSubmitting,
  onEditPaymentDetails,
  paymentDetails,
  setSubmitting,
  submitForm,
  validateForm,
  values,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const isStripeInitialized = Boolean(stripe && elements);

  const [isPaymentSuccessful, setPaymentSuccessful] = useState(false);

  const idempotencyKey = useMemo(
    () => nanoid(),
    // We want this key to be regenerated every time the amount changes, so
    // a new payment intent that reflects the updated amount will be created.
    // Otherwise, going back and updating the amount in the previous form would
    // result in paying the previous (incorrect) amount via Stripe since the
    // idempotency key is still the same as before.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paymentDetails.amount_usd],
  );

  const handleEditPaymentDetails = useCallback(() => {
    onEditPaymentDetails(values);
  }, [onEditPaymentDetails, values]);

  const [, createPaymentIntent] = useMutation(() =>
    paymentIntentQuery(window.el8Globals.PRACTICE_ID, {
      amount_usd: paymentDetails.amount_usd,
      idempotency_key: idempotencyKey,
    }),
  );

  const [, confirmPaymentIntent] = useMutation((paymentIntentId: string) => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      amount_usd,
      ...rest
    } = paymentDetails;

    return confirmPaymentIntentQuery(paymentIntentId, rest);
  });
  const logPayPaymentSiteSubmitPaymentClick = useCallback(() => {
    logEvent('pay payment site', {
      'payment amount': paymentDetails.amount_usd,
      'statement/invoice #': paymentDetails.invoice_number.length > 0,
      notes: paymentDetails.notes.length > 0,
    });
  }, []);

  const confirmCardPayment = useConfirmStripeCardPayment();
  const handleSubmit = async (): Promise<void> => {
    if (!isStripeInitialized) return;
    logPayPaymentSiteSubmitPaymentClick();
    let isSuccess = false;
    try {
      await fixedSubmitForm({ submitForm, validateForm });

      try {
        const paymentIntentResponse = await createPaymentIntent();
        checkSuccess(paymentIntentResponse);
        const paymentIntent = paymentIntentResponse.body as CreatePaymentIntentResponseBody;

        const stripePaymentSucceeded = await confirmCardPayment(
          paymentIntent.client_secret,
          makeConfirmCardPaymentData(elements, { ...values, save_card: false }),
        );
        if (!stripePaymentSucceeded) return;

        if (!isPaymentSuccessful) setPaymentSuccessful(true);

        const confirmPaymentIntentResponse = await confirmPaymentIntent(paymentIntent.id);
        checkSuccess(confirmPaymentIntentResponse);

        window.location.href = (
          confirmPaymentIntentResponse.body as ConfirmPaymentIntentResponseBody
        ).payment_charge.receipt_page_url;
        isSuccess = true;
      } catch (err) {
        notify('There was an error making the payment. Please try again.');
      }
    } finally {
      // If the entire flow succeeds, we don't want to set submitting back to false.
      // We want everything to stay disabled while the user is redirected to the receipt page,
      // so the user can't click Pay again while the redirect is in progress
      if (!isSuccess) {
        setSubmitting(false);
      }
    }
  };

  const amountText = formatDollars(paymentDetails.amount_usd);

  return (
    <RootContainer>
      <PaymentsHeader />
      <Box justify="space-between">
        <HeaderText legacyVariant="legacyHeaderText" tag="h1">
          Review details
        </HeaderText>
        <LinkButton
          disabled={isSubmitting || isPaymentSuccessful}
          onClick={handleEditPaymentDetails}
          text="Edit"
        />
      </Box>
      <Marquee className={cn(styles.paymentSummary)}>
        <table>
          <tbody>
            <tr>
              <td>
                <Text appearance="system">Patient name</Text>
              </td>
              <td>
                <Text>
                  {paymentDetails.patient_first_name} {paymentDetails.patient_last_name}
                </Text>
              </td>
            </tr>
            {Boolean(paymentDetails.invoice_number) && (
              <tr>
                <td>
                  <Text appearance="system">Statement / Invoice / Account #</Text>
                </td>
                <td>
                  <Text>{paymentDetails.invoice_number}</Text>
                </td>
              </tr>
            )}
            <tr>
              <td>
                <Text appearance="system">Amount</Text>
              </td>
              <td>
                <Text>{amountText}</Text>
              </td>
            </tr>
            {Boolean(paymentDetails.notes) && (
              <tr>
                <td>
                  <Text appearance="system">Notes</Text>
                </td>
                <td>
                  <Text>{paymentDetails.notes}</Text>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Marquee>
      <PaymentMethodForm />
      <Button
        kind="primary"
        disabled={!(stripe && elements)}
        loading={isSubmitting}
        style={{ width: '100%' }}
        onClick={handleSubmit}
        text={`Pay ${amountText}`}
      />
      <Box justify="center" style={{ marginTop: 8 }}>
        <LockIcon style={{ marginRight: 4 }} />
        <Text appearance="system">Secure payments powered by Elation Health & Stripe</Text>
      </Box>
    </RootContainer>
  );
};

export default withFormik(withFormikConfig)(SubmitPaymentStep);
